import { useEffect, useMemo, useState, type ComponentType } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { RefreshCw, X } from "lucide-react";
import type {
  CareerMove,
  CareerMoveActionResult,
  CareerMoveBucket,
} from "@career-ops-amplify/shared";
import { useCareerMoves, useCareerMoveAction } from "@/hooks/useCareer";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { useSyncCursorPage } from "@/hooks/useSyncCursorPage";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { PaginationBar } from "@/components/pagination-bar";
import { Modal } from "@/components/ui/modal";
import { CareerSummaryStrip, type SummaryStripKey } from "./career-summary-strip";
import { CareerMoveBucketSelector } from "./career-move-bucket-selector";
import { CareerMoveCard, type MovesDensity } from "./career-move-card";
import { CareerMoveDetailPanel } from "./career-move-detail-panel";
import { CareerMoveEmptyState } from "./career-move-empty-state";
import { CareerMoveExecutionFeedback } from "./career-move-execution-feedback";

const HOME_BUCKETS: Array<CareerMoveBucket | "ALL_ACTIVE"> = [
  "ALL_ACTIVE",
  "ACT_NOW",
  "REPLY_NEEDED",
  "READY_TO_ADVANCE",
  "AT_RISK",
  "WAITING",
  "NURTURE",
];

const DENSITY_STORAGE_KEY = "career-ops:moves-density";

function loadDensity(): MovesDensity {
  try {
    const raw = localStorage.getItem(DENSITY_STORAGE_KEY);
    if (raw === "compact" || raw === "roomy") return raw;
  } catch {
    // ignore
  }
  return "roomy";
}

function parseHomeBucket(
  raw: string | null,
): CareerMoveBucket | "ALL_ACTIVE" | null {
  if (!raw) return null;
  return HOME_BUCKETS.includes(raw as CareerMoveBucket | "ALL_ACTIVE")
    ? (raw as CareerMoveBucket | "ALL_ACTIVE")
    : null;
}

function primaryHref(move: CareerMove): string {
  return move.actionDefinition?.href ?? move.sourceLinks?.[0]?.href ?? "/";
}

type Props = {
  OperationalHomeFallback?: ComponentType;
};

export function CareerCommandHome({ OperationalHomeFallback }: Props) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const bucket =
    parseHomeBucket(searchParams.get("bucket")) ?? "ALL_ACTIVE";
  const selectedId = searchParams.get("move");
  const [feedback, setFeedback] = useState<CareerMoveActionResult | null>(null);
  const [summaryFilter, setSummaryFilter] = useState<SummaryStripKey | null>(
    null,
  );
  const [density, setDensity] = useState<MovesDensity>(() => loadDensity());
  const isDesktop = useIsDesktop();

  const cursorPag = useCursorPagination([bucket], { initialPageSize: 10 });

  function patchHomeParams(
    patch: {
      move?: string | null;
      bucket?: CareerMoveBucket | "ALL_ACTIVE";
    },
    replace = true,
  ) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if ("move" in patch) {
          if (patch.move) next.set("move", patch.move);
          else next.delete("move");
        }
        if ("bucket" in patch && patch.bucket) {
          if (patch.bucket === "ALL_ACTIVE") next.delete("bucket");
          else next.set("bucket", patch.bucket);
        }
        // Drop legacy cursor query param if present
        next.delete("cursor");
        return next;
      },
      { replace },
    );
  }

  function setDensityPreference(next: MovesDensity) {
    setDensity(next);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }

  const query = useCareerMoves({
    bucket,
    limit: cursorPag.pageSize,
    cursor: cursorPag.cursor,
  });
  useSyncCursorPage(
    cursorPag.setNextCursor,
    query.data,
    !query.isLoading && !query.isFetching && !!query.data,
  );
  const actionMut = useCareerMoveAction();

  const data = query.data;
  const moves = data?.moves ?? [];
  const selected = moves.find((m) => m.id === selectedId) ?? null;

  useEffect(() => {
    if (!isDesktop) return;
    if (query.isLoading || query.isFetching) return;
    if (!moves.length) return;
    if (selectedId && moves.some((m) => m.id === selectedId)) return;
    patchHomeParams({ move: moves[0]?.id ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moves, selectedId, query.isLoading, query.isFetching, isDesktop]);

  const emptyVariant = useMemo(() => {
    if (!data?.featureEnabled) return null;
    if (moves.length === 0 && bucket === "ALL_ACTIVE" && cursorPag.page === 1) {
      return "all_clear" as const;
    }
    if (moves.length === 0 && cursorPag.page === 1) {
      return "bucket_empty" as const;
    }
    return null;
  }, [data?.featureEnabled, moves.length, bucket, cursorPag.page]);

  if (data && data.featureEnabled === false && OperationalHomeFallback) {
    return <OperationalHomeFallback />;
  }

  async function runAction(
    move: CareerMove,
    action: string,
    extras?: { reason?: string },
  ) {
    try {
      const result = await actionMut.mutateAsync({
        id: move.id,
        action,
        materialStateHash: move.materialStateHash,
        generationId: move.generationId,
        reason: extras?.reason,
      });
      setFeedback(result);
      patchHomeParams({ move: null });
    } catch (err) {
      setFeedback({
        ok: false,
        fingerprint: move.fingerprint,
        action,
        message: err instanceof Error ? err.message : "Action failed",
      });
    }
  }

  function onPrimary(move: CareerMove) {
    void runAction(move, "EXECUTE_PRIMARY").then(() => {
      navigate(primaryHref(move));
    });
  }

  function onAskAi(move: CareerMove) {
    const sp = new URLSearchParams({
      careerMoveId: move.id,
      generationId: move.generationId,
      originBucket: move.bucket,
    });
    if (move.jobId) sp.set("jobId", move.jobId);
    if (move.pipelineId) sp.set("pipelineId", move.pipelineId);
    navigate(`/ai-assistant?${sp.toString()}`);
  }

  const showPagination =
    moves.length > 0 || cursorPag.page > 1 || cursorPag.hasMore;

  return (
    <div className="pb-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Moves</h1>
          <p className="mt-1 text-muted-foreground">
            Your highest-impact career moves
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/analytics"
            className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/20"
          >
            Open Analytics
          </Link>
          <Link
            to="/goals"
            className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/20"
          >
            Goals
          </Link>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground transition hover:bg-muted/20"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
      </header>

      {data?.summary ? (
        <CareerSummaryStrip
          summary={data.summary}
          activeFilter={summaryFilter}
          onFilter={(filter, nextBucket) => {
            setSummaryFilter(filter);
            if (nextBucket) {
              cursorPag.reset();
              patchHomeParams({
                bucket: nextBucket,
                move: null,
              });
            }
          }}
          onNavigate={(key) => {
            if (key === "goals") navigate("/goals");
          }}
        />
      ) : null}

      <CareerMoveBucketSelector
        bucket={bucket}
        counts={data?.bucketCounts}
        density={density}
        onDensityChange={setDensityPreference}
        onChange={(next) => {
          cursorPag.reset();
          patchHomeParams({ bucket: next, move: null });
          setSummaryFilter(null);
        }}
      />

      <CareerMoveExecutionFeedback
        feedback={feedback}
        onDismiss={() => setFeedback(null)}
      />

      {query.isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
        </div>
      ) : null}

      {query.error && !query.isLoading ? (
        <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load career moves. Please try again.
        </div>
      ) : null}

      {!query.isLoading && emptyVariant ? (
        <CareerMoveEmptyState variant={emptyVariant} />
      ) : null}

      {!query.isLoading && (moves.length > 0 || cursorPag.page > 1) ? (
        <>
          <div className="hidden gap-4 lg:grid lg:grid-cols-[minmax(0,45%)_minmax(0,1fr)] lg:items-start">
            <div className="max-h-[calc(100vh-8rem)] space-y-2 overflow-y-auto overscroll-contain pr-1">
              {moves.map((m) => (
                <CareerMoveCard
                  key={m.id}
                  move={m}
                  density={density}
                  selected={selected?.id === m.id}
                  onSelect={() => patchHomeParams({ move: m.id })}
                />
              ))}
              {showPagination ? (
                <PaginationBar
                  mode="cursor"
                  page={cursorPag.page}
                  pageSize={cursorPag.pageSize}
                  itemCount={moves.length}
                  showingFrom={
                    moves.length === 0 ? 0 : cursorPag.showingFrom
                  }
                  showingTo={cursorPag.showingTo(moves.length)}
                  hasMore={cursorPag.hasMore}
                  canPrev={cursorPag.canPrev}
                  onFirst={() => {
                    cursorPag.goFirst();
                    patchHomeParams({ move: null });
                  }}
                  onPrev={() => {
                    cursorPag.goPrev();
                    patchHomeParams({ move: null });
                  }}
                  onNext={() => {
                    cursorPag.goNext();
                    patchHomeParams({ move: null });
                  }}
                  onPageSizeChange={(size) => {
                    cursorPag.setPageSize(size);
                    patchHomeParams({ move: null });
                  }}
                />
              ) : null}
            </div>
            <div className="sticky top-14 self-start max-h-[calc(100vh-4.5rem)] overflow-y-auto overscroll-contain">
              {selected ? (
                <CareerMoveDetailPanel
                  move={selected}
                  busy={actionMut.isPending}
                  onPrimary={() => onPrimary(selected)}
                  onLater={() => void runAction(selected, "LATER")}
                  onDismiss={() => void runAction(selected, "DISMISS")}
                  onDelegate={() => void runAction(selected, "DELEGATE")}
                  onNotRight={(reason) =>
                    void runAction(selected, "NOT_RIGHT", { reason })
                  }
                  onAskAi={() => onAskAi(selected)}
                />
              ) : (
                <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
                  Select a move to review
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3 lg:hidden">
            {moves.map((m) => (
              <CareerMoveCard
                key={m.id}
                move={m}
                density={density}
                selected={selected?.id === m.id}
                onSelect={() => patchHomeParams({ move: m.id })}
              />
            ))}
            {showPagination ? (
              <PaginationBar
                mode="cursor"
                page={cursorPag.page}
                pageSize={cursorPag.pageSize}
                itemCount={moves.length}
                showingFrom={moves.length === 0 ? 0 : cursorPag.showingFrom}
                showingTo={cursorPag.showingTo(moves.length)}
                hasMore={cursorPag.hasMore}
                canPrev={cursorPag.canPrev}
                onFirst={() => {
                  cursorPag.goFirst();
                  patchHomeParams({ move: null });
                }}
                onPrev={() => {
                  cursorPag.goPrev();
                  patchHomeParams({ move: null });
                }}
                onNext={() => {
                  cursorPag.goNext();
                  patchHomeParams({ move: null });
                }}
                onPageSizeChange={(size) => {
                  cursorPag.setPageSize(size);
                  patchHomeParams({ move: null });
                }}
              />
            ) : null}
          </div>

          {!isDesktop && selected ? (
            <Modal
              open
              onClose={() => patchHomeParams({ move: null })}
              className="lg:hidden"
              panelClassName="max-h-[90vh]"
              labelledBy="moves-mobile-detail-title"
            >
              <div className="flex shrink-0 items-center gap-2 border-b border-border px-4 py-3">
                <h2
                  id="moves-mobile-detail-title"
                  className="min-w-0 flex-1 truncate text-base font-semibold text-foreground"
                >
                  Recommended move
                </h2>
                <button
                  type="button"
                  onClick={() => patchHomeParams({ move: null })}
                  aria-label="Close"
                  className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-1.5 text-muted-foreground transition hover:bg-muted/20 hover:text-foreground"
                >
                  <X className="size-5" />
                </button>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                <CareerMoveDetailPanel
                  move={selected}
                  busy={actionMut.isPending}
                  className="rounded-none border-0"
                  onPrimary={() => onPrimary(selected)}
                  onLater={() => void runAction(selected, "LATER")}
                  onDismiss={() => void runAction(selected, "DISMISS")}
                  onDelegate={() => void runAction(selected, "DELEGATE")}
                  onNotRight={(reason) =>
                    void runAction(selected, "NOT_RIGHT", { reason })
                  }
                  onAskAi={() => onAskAi(selected)}
                />
              </div>
            </Modal>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
